/**
 * CheckInCalendarTab Component
 * Calendar view showing check-in due dates and completions
 * Supports month and week view toggle
 */

import React, { useMemo } from 'react';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { useUrlState } from '../../hooks/useUrlState';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO
} from 'date-fns';
import { Card, LoadingSpinner } from '../../components/common';
import { useCalendarData } from '../../hooks';
import type { CalendarEvent, CheckInStatus } from '../../types/okr';

type ViewMode = 'month' | 'week';

const statusConfig: Record<CheckInStatus, { icon: typeof CheckCircle; color: string; bgColor: string }> = {
  on_track: { icon: CheckCircle, color: 'text-success-600', bgColor: 'bg-success-100' },
  at_risk: { icon: AlertTriangle, color: 'text-warning-600', bgColor: 'bg-warning-100' },
  off_track: { icon: AlertCircle, color: 'text-danger-600', bgColor: 'bg-danger-100' },
};

interface DayEventsProps {
  date: Date;
  events: CalendarEvent[];
  isCurrentMonth?: boolean;
  viewMode: ViewMode;
  onEventClick: (event: CalendarEvent) => void;
}

const DayEvents: React.FC<DayEventsProps> = ({
  date,
  events,
  isCurrentMonth = true,
  viewMode,
  onEventClick
}) => {
  const dayEvents = events.filter(e => isSameDay(parseISO(e.date), date));
  const isWeekView = viewMode === 'week';
  const maxVisibleEvents = isWeekView ? 10 : 3;
  const visibleEvents = dayEvents.slice(0, maxVisibleEvents);
  const remainingCount = dayEvents.length - maxVisibleEvents;

  return (
    <div
      className={`
        ${isWeekView ? 'min-h-[200px]' : 'min-h-[100px]'} p-1 border-r border-b border-stone-200
        ${!isCurrentMonth ? 'bg-stone-50' : 'bg-white'}
        ${isToday(date) ? 'ring-2 ring-inset ring-primary-500' : ''}
      `}
    >
      {/* Day number */}
      <div className={`
        text-sm font-medium mb-1 px-1
        ${isToday(date) ? 'text-primary-600' : !isCurrentMonth ? 'text-stone-400' : 'text-stone-700'}
      `}>
        {format(date, 'd')}
      </div>

      {/* Events */}
      <div className="space-y-1">
        {visibleEvents.map((event) => {
          const isDue = event.type === 'due';
          const isOverdue = event.isOverdue;
          const statusInfo = event.status ? statusConfig[event.status] : null;

          return (
            <button
              key={event.id}
              onClick={() => onEventClick(event)}
              className={`
                w-full text-left px-1.5 py-0.5 rounded text-xs truncate transition-colors
                ${isDue
                  ? isOverdue
                    ? 'bg-danger-100 text-danger-700 hover:bg-danger-200'
                    : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                  : statusInfo
                    ? `${statusInfo.bgColor} ${statusInfo.color} hover:opacity-80`
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }
              `}
              title={`${event.okrTitle} - ${isDue ? 'Due' : 'Completed'}`}
            >
              <span className="flex items-center gap-1">
                {isDue ? (
                  <Clock className="h-3 w-3 flex-shrink-0" />
                ) : statusInfo ? (
                  <statusInfo.icon className="h-3 w-3 flex-shrink-0" />
                ) : (
                  <CheckCircle className="h-3 w-3 flex-shrink-0" />
                )}
                <span className="truncate">{event.okrTitle}</span>
              </span>
            </button>
          );
        })}
        {remainingCount > 0 && (
          <div className="text-xs text-stone-500 px-1">
            +{remainingCount} more
          </div>
        )}
      </div>
    </div>
  );
};

/** `yyyy-MM-dd` from the URL as a local date; empty or malformed means today. */
const parseCalendarDate = (raw: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

export const CheckInCalendarTab: React.FC = () => {
  const openWithReturn = useOpenWithReturn();
  // Shown date and Month/Week live in the URL, so opening an OKR and coming Back keeps them.
  // An empty `cdate` means today.
  const [view, setView] = useUrlState({ cdate: '', cview: 'month' }, { cview: ['month', 'week'] });
  const viewMode = view.cview as ViewMode;
  const setViewMode = (mode: ViewMode) => setView({ cview: mode });
  const currentDate = useMemo(() => parseCalendarDate(view.cdate), [view.cdate]);
  // Functional, from the newest URL value: fast Previous/Next clicks must each count.
  const setCurrentDate = (update: (prev: Date) => Date) =>
    setView((cur) => ({ cdate: format(update(parseCalendarDate(cur.cdate)), 'yyyy-MM-dd') }));

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      // Include days from prev/next month to fill the calendar grid
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
      return {
        start: calendarStart,
        end: calendarEnd,
        displayStart: monthStart,
        displayEnd: monthEnd
      };
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
      return {
        start: weekStart,
        end: weekEnd,
        displayStart: weekStart,
        displayEnd: weekEnd
      };
    }
  }, [currentDate, viewMode]);

  // Fetch calendar data
  const { data: calendarData, isLoading, isPlaceholderData, error, refetch } = useCalendarData(
    format(dateRange.start, 'yyyy-MM-dd'),
    format(dateRange.end, 'yyyy-MM-dd')
  );

  // Generate days array
  const days = useMemo(() => {
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange]);

  // Navigation handlers
  const handlePrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(prev => subMonths(prev, 1));
    } else {
      setCurrentDate(prev => subWeeks(prev, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(prev => addMonths(prev, 1));
    } else {
      setCurrentDate(prev => addWeeks(prev, 1));
    }
  };

  const handleToday = () => {
    setView({ cdate: '' });
  };

  const handleEventClick = (event: CalendarEvent) => {
    openWithReturn(`/okrs/${event.okrId}`);
  };

  // Stats. While the next range loads, `calendarData` is still the PREVIOUS range's
  // (placeholderData), so these counts are hidden then — see the Stats block below.
  // Its events may stay on the grid: each lands only on its own date.
  const stats = useMemo(() => {
    if (!calendarData?.events) return { due: 0, completed: 0, overdue: 0 };
    return {
      due: calendarData.events.filter(e => e.type === 'due').length,
      completed: calendarData.events.filter(e => e.type === 'completed').length,
      overdue: calendarData.events.filter(e => e.isOverdue).length
    };
  }, [calendarData]);

  // Full-page spinner on the very first load only. After that the calendar stays
  // mounted, so Previous/Next keep receiving clicks while a range loads.
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading calendar..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-danger-600">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          <p>Failed to load calendar data. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  const events = calendarData?.events || [];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg overflow-hidden border border-stone-200">
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'month'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-stone-600 hover:bg-stone-50'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'week'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-stone-600 hover:bg-stone-50'
                }`}
              >
                Week
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleToday}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Today
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevious}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-600"
                aria-label={`Previous ${viewMode}`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-lg font-semibold text-stone-900 min-w-[160px] text-center">
                {viewMode === 'month'
                  ? format(currentDate, 'MMMM yyyy')
                  : `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`
                }
              </span>
              <button
                onClick={handleNext}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-600"
                aria-label={`Next ${viewMode}`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Stats */}
          {isPlaceholderData ? (
            <div className="flex items-center gap-2 text-sm text-stone-500" role="status">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
              Loading…
            </div>
          ) : (
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-primary-200"></span>
              <span className="text-stone-600">{stats.due} Due</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-success-200"></span>
              <span className="text-stone-600">{stats.completed} Completed</span>
            </div>
            {stats.overdue > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-danger-200"></span>
                <span className="text-danger-600 font-medium">{stats.overdue} Overdue</span>
              </div>
            )}
          </div>
          )}
        </div>
      </Card>

      {/* Calendar Grid */}
      <Card
        className={`overflow-hidden transition-opacity ${isPlaceholderData ? 'opacity-60' : ''}`}
        aria-busy={isPlaceholderData}
      >
        {/* Week day headers */}
        <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50">
          {weekDays.map((day) => (
            <div
              key={day}
              className="py-2 text-center text-sm font-medium text-stone-600 border-r last:border-r-0 border-stone-200"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {days.map((day) => (
            <DayEvents
              key={day.toISOString()}
              date={day}
              events={events}
              isCurrentMonth={viewMode === 'week' || isSameMonth(day, currentDate)}
              viewMode={viewMode}
              onEventClick={handleEventClick}
            />
          ))}
        </div>
      </Card>

      {/* Legend */}
      <Card className="p-4">
        <h4 className="text-sm font-medium text-stone-700 mb-3">Legend</h4>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-primary-100 text-primary-700">
              <Clock className="h-3 w-3" />
              Due
            </span>
            <span className="text-stone-500">Upcoming check-in due date</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-danger-100 text-danger-700">
              <Clock className="h-3 w-3" />
              Overdue
            </span>
            <span className="text-stone-500">Past due date</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-success-100 text-success-700">
              <CheckCircle className="h-3 w-3" />
              On Track
            </span>
            <span className="text-stone-500">Completed check-in</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-warning-100 text-warning-700">
              <AlertTriangle className="h-3 w-3" />
              At Risk
            </span>
            <span className="text-stone-500">Check-in flagged as at risk</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-danger-100 text-danger-700">
              <AlertCircle className="h-3 w-3" />
              Off Track
            </span>
            <span className="text-stone-500">Check-in flagged as off track</span>
          </div>
        </div>
      </Card>
    </div>
  );
};
